// modules/ItemDataLoader.js
// 商品数据加载器模块 - 集中管理所有商品数据

import DebugHelper from './DebugHelper.js';
import ErrorHandler from './ErrorHandler.js';
import { deepClone } from './utils/PlayerDataSync.js';

export default class ItemDataLoader {
    /**
     * 商品数据缓存
     * @type {Object<string, Object>}
     */
    static itemsCache = null;
    
    /**
     * 战车装备数据缓存
     * @type {Object<string, Object>}
     */
    static vehicleEquipmentCache = null;
    
    /**
     * 初始化商品数据（从Phaser缓存中加载）
     * @param {Phaser.Scene} scene - 场景对象
     * @returns {boolean} 是否加载成功
     */
    static initialize(scene) {
        try {
            // 从Phaser缓存中获取商品数据
            const itemsData = scene.cache.json.get('items');
            
            if (!itemsData || !itemsData.items) {
                DebugHelper.warn('ItemDataLoader: 商品数据文件格式错误或不存在');
            } else {
                // 将商品数组转换为以id为key的对象，方便查找
                this.itemsCache = {};
                itemsData.items.forEach(item => {
                    if (!item.id) {
                        DebugHelper.warn('ItemDataLoader: 发现没有id的商品，已跳过', item);
                        return;
                    }
                    this.itemsCache[item.id] = item;
                });
                
                DebugHelper.log(`ItemDataLoader: 成功加载 ${Object.keys(this.itemsCache).length} 个商品`);
            }
            
            // 加载战车装备数据
            const vehicleEquipmentData = scene.cache.json.get('vehicle_equipment');
            if (vehicleEquipmentData && vehicleEquipmentData.vehicleEquipment) {
                this.vehicleEquipmentCache = {};
                vehicleEquipmentData.vehicleEquipment.forEach(equipment => {
                    if (!equipment.id) {
                        DebugHelper.warn('ItemDataLoader: 发现没有id的战车装备，已跳过', equipment);
                        return;
                    }
                    this.vehicleEquipmentCache[equipment.id] = equipment;
                });
                
                DebugHelper.log(`ItemDataLoader: 成功加载 ${Object.keys(this.vehicleEquipmentCache).length} 个战车装备`);
            }
            
            return true;
        } catch (error) {
            ErrorHandler.handleError(error, 'ItemDataLoader初始化');
            return false;
        }
    }
    
    /**
     * 根据ID获取商品数据
     * @param {string} itemId - 商品ID
     * @returns {Object|null} 商品数据，不存在返回null
     */
    static getItemById(itemId) {
        if (!this.itemsCache) {
            DebugHelper.warn('ItemDataLoader: 商品数据未初始化');
            return null;
        }
        
        const item = this.itemsCache[itemId];
        if (!item) {
            DebugHelper.warn(`ItemDataLoader: 未找到ID为 ${itemId} 的商品`);
            return null;
        }
        
        // 返回深拷贝，避免修改原始数据
        return deepClone(item);
    }
    
    /**
     * 根据ID数组获取多个商品数据（支持普通物品和战车装备）
     * @param {Array<string>} itemIds - 商品ID数组
     * @returns {Array<Object>} 商品数据数组
     */
    static getItemsByIds(itemIds) {
        if (!Array.isArray(itemIds)) {
            DebugHelper.warn('ItemDataLoader: getItemsByIds 需要传入数组');
            return [];
        }
        
        const items = [];
        itemIds.forEach(id => {
            // 先从普通物品中查找
            let item = this.getItemById(id);
            
            // 如果没找到，从战车装备中查找
            if (!item && this.vehicleEquipmentCache) {
                item = this.vehicleEquipmentCache[id];
                if (item) {
                    // 返回深拷贝
                    item = deepClone(item);
                }
            }
            
            if (item) {
                items.push(item);
            } else {
                DebugHelper.warn(`ItemDataLoader: 未找到ID为 ${id} 的物品或装备`);
            }
        });
        
        return items;
    }
    
    /**
     * 获取所有商品数据
     * @returns {Array<Object>} 所有商品数据数组
     */
    static getAllItems() {
        if (!this.itemsCache) {
            DebugHelper.warn('ItemDataLoader: 商品数据未初始化');
            return [];
        }
        
        return Object.values(this.itemsCache).map(item => deepClone(item));
    }
    
    /**
     * 检查商品数据是否已初始化
     * @returns {boolean} 是否已初始化
     */
    static isInitialized() {
        return this.itemsCache !== null;
    }
}

